import * as Linking from 'expo-linking'
import { compact, join, map } from 'lodash'
import { forwardRef, useCallback, useMemo } from 'react'
import { View } from 'react-native'
import { graphql, useFragment } from 'react-relay'

import { type ProfileCard_profile$key } from '@/__generated__/ProfileCard_profile.graphql'
import { type ProfileCard_query$key } from '@/__generated__/ProfileCard_query.graphql'
import instagramSvg from '@/assets/images/instagram.svg'
import {
  Button,
  ContainedCard,
  HStack,
  Icon,
  PhotoCarousel,
  Pressable,
  ProfileVerifiedBadge,
  Spinner,
  Text,
  VStack,
  type ContainedCardProps
} from '@/components'
import { useHasMetricLocale, useProfilePhotos, useSearchDistanceValues } from '@/hooks'
import { COLOR_WHITE, getPublicImageUrl, IS_WEB } from '@/lib'

export interface ProfileCardProps extends ContainedCardProps {
  isLoading?: boolean
  profileRef: ProfileCard_profile$key | null | undefined
  queryRef: ProfileCard_query$key | null | undefined
  withAccessibilityHeader?: boolean
  withDescription?: boolean
}

export const ProfileCard = forwardRef<View, ProfileCardProps>(
  ({ children, isLoading = false, profileRef, queryRef, withAccessibilityHeader, withDescription, ...props }, ref) => {
    const hasMetricLocale = useHasMetricLocale()
    const profileData = useFragment(
      graphql`
        fragment ProfileCard_profile on Profile {
          age
          closestCity
          distanceInKilometers
          distanceInMiles
          instagram
          isVerified
          name
          rowId
          userId
          ...ProfileVerifiedBadge_profile
          ...useProfilePhotos_profile
        }
      `,
      profileRef
    )
    const queryData = useFragment(
      graphql`
        fragment ProfileCard_query on Query {
          currentUser {
            isMetricSystem
            rowId
          }
        }
      `,
      queryRef
    )
    const { profilePhotosConnection } = useProfilePhotos(profileData)
    const isMetricSystem = queryData?.currentUser?.isMetricSystem ?? hasMetricLocale
    const { minValue: minSearchDistance, unitsShort } = useSearchDistanceValues(isMetricSystem)
    const openSettings = useCallback(async () => {
      try {
        const instagramAppUrl = `instagram://user?username=${profileData?.instagram}`
        const instagramWebUrl = `https://www.instagram.com/${profileData?.instagram}`

        if (IS_WEB) {
          window.open(instagramWebUrl, '_blank', 'noopener,noreferrer')
          return
        }

        const hasInstagram = await Linking.canOpenURL(instagramAppUrl)

        return Linking.openURL(hasInstagram ? instagramAppUrl : instagramWebUrl)
      } catch (err) {
        console.error('[linking] error', err)
      }
    }, [profileData?.instagram])

    const distance = useMemo(
      () => (isMetricSystem ? profileData?.distanceInKilometers : profileData?.distanceInMiles),
      [isMetricSystem, profileData?.distanceInKilometers, profileData?.distanceInMiles]
    )
    const locationText = useMemo(
      () =>
        profileData?.closestCity || distance
          ? join(
              compact([
                profileData?.closestCity,
                distance
                  ? distance > minSearchDistance
                    ? ` (${Math.round(distance)} ${unitsShort})`
                    : ` Less than ${minSearchDistance} ${unitsShort} away`
                  : null
              ]),
              ' '
            )
          : undefined,
      [distance, minSearchDistance, profileData?.closestCity, unitsShort]
    )
    const accessibilityLabel = useMemo(() => {
      let accessibilityLabel = `Profile Card. Name: ${profileData?.name}, Age: ${profileData?.age}`

      if (locationText) {
        accessibilityLabel = `${accessibilityLabel}, Location: ${locationText}`
      }

      if (profileData?.isVerified) {
        accessibilityLabel = `${accessibilityLabel}, Verified`
      }

      return accessibilityLabel
    }, [locationText, profileData?.age, profileData?.isVerified, profileData?.name])

    return (
      <ContainedCard
        accessibilityState={{
          busy: isLoading
        }}
        {...props}
      >
        {withAccessibilityHeader ? (
          <Button
            action='default'
            accessibilityRole='header'
            accessibilityLabel={accessibilityLabel}
            aria-label={accessibilityLabel}
            aria-level={2}
            className='sr-only w-full'
            ref={ref}
            role='heading'
            variant='link'
          />
        ) : null}
        <PhotoCarousel
          data={useMemo(
            () =>
              compact(
                map(profilePhotosConnection?.edges, (edge) => {
                  const uri = getPublicImageUrl(edge?.node?.storageObject?.bucket, edge?.node?.storageObject?.key)

                  return {
                    blurhash: edge?.node?.storageObject?.blurhash,
                    description: edge?.node?.description,
                    rowId: edge?.node?.rowId,
                    source: uri
                      ? {
                          uri
                        }
                      : null
                  }
                })
              ),
            [profilePhotosConnection?.edges]
          )}
          ref={withAccessibilityHeader ? undefined : ref}
          withRoundCorners
        >
          {children}
        </PhotoCarousel>
        {withDescription ? (
          <VStack
            accessibilityElementsHidden
            aria-hidden
            importantForAccessibility='no-hide-descendants'
          >
            <HStack className='items-start justify-between'>
              <HStack className='items-center justify-start'>
                <Text className='font-poppinsSemiBold text-xl text-black'>
                  {profileData?.name}, {profileData?.age}
                </Text>
              </HStack>
              <HStack className='items-center justify-end gap-1'>
                {profileData?.instagram && (
                  <Pressable
                    accessibilityHint='This will open in the Instagram app, if it is on your device.'
                    accessibilityLabel={`Open ${profileData?.name ?? 'this profile'}'s Instagram profile`}
                    accessibilityRole='button'
                    onPress={openSettings}
                  >
                    <Icon
                      as={instagramSvg}
                      size='xl'
                    />
                  </Pressable>
                )}
                <ProfileVerifiedBadge profileRef={profileData} />
              </HStack>
            </HStack>
            <HStack className='min-h-5'>
              {locationText && <Text className='font-poppinsMedium text-sm text-black'>{locationText}</Text>}
            </HStack>
          </VStack>
        ) : null}
        {isLoading ? (
          <View
            accessibilityElementsHidden
            aria-hidden
            className='absolute inset-x-0 inset-y-0 z-50 flex items-center justify-center bg-white/80'
            importantForAccessibility='no-hide-descendants'
          >
            <Spinner
              color={COLOR_WHITE}
              size='large'
            />
          </View>
        ) : null}
      </ContainedCard>
    )
  }
)

ProfileCard.displayName = 'ProfileCard'
